import { getApkDownloadUrl } from "@utils/api-communication/RestApiCli";
import type { HardwareModels } from "types/productTypes";
import { apkBaseUrl, downloadApk, getDownloadUrlForLog } from "./ADBUtils";
import { getVersionedApkFilename } from "./apkNaming";
import { errorMessage } from "@utils/errorMessage";

const APK_URL_LOOKUP_TIMEOUT_MS = 3000;

type Log = (message: string) => void;
type DownloadProgress = Parameters<typeof downloadApk>[3];

function resolveApkUrl(
  version: string,
  hardwareModel: HardwareModels | null,
  fallbackUrl: string,
  log: Log,
): Promise<string> {
  return new Promise((resolve) => {
    let settled = false;
    const resolveOnce = (url: string) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      resolve(url);
    };
    const timeoutId = window.setTimeout(() => {
      log("Backend APK URL lookup timed out, falling back to public bucket URL");
      resolveOnce(fallbackUrl);
    }, APK_URL_LOOKUP_TIMEOUT_MS);

    getApkDownloadUrl(
      version,
      (response) => {
        if (settled) return;
        const data = response?.data;
        if (data?.exists && data?.url) {
          log("Using backend-provided APK URL");
          resolveOnce(data.url);
        } else {
          log("Backend APK not found, falling back to public bucket URL");
          resolveOnce(fallbackUrl);
        }
      },
      (error) => {
        if (settled) return;
        log(
          "Backend APK URL lookup failed, falling back to public bucket URL: " +
            errorMessage(error),
        );
        resolveOnce(fallbackUrl);
      },
      undefined,
      hardwareModel ?? undefined,
    );
  });
}

export async function downloadReleaseApk(
  version: string,
  hardwareModel: HardwareModels | null,
  log: Log,
  onProgress?: DownloadProgress,
) {
  const fallbackUrl = `${apkBaseUrl}${getVersionedApkFilename(hardwareModel, version)}`;
  const url = await resolveApkUrl(version, hardwareModel, fallbackUrl, log);
  log(`Download requested: ${getDownloadUrlForLog(url)}`);
  try {
    return await downloadApk(url, undefined, log, onProgress);
  } catch (error) {
    if (url === fallbackUrl) throw error;
    log("Download via backend URL failed, retrying with public bucket URL: " + errorMessage(error));
    return downloadApk(fallbackUrl, undefined, log, onProgress);
  }
}
